import React, { useEffect, useState } from "react";
import type {
  HeatmapNode,
  HotTopicCard,
  MarketIndex,
  MarketRecap,
  NewsCategoryOption,
  PreviewNewsCard,
  SelectedCategory,
} from "../../features/kquant/types";
import { mockApi } from "../../features/kquant/api/mockApi";
import { FormControlStyles } from "../../components/ui/FormControlStyles";
import { Background } from "../../features/kquant/components/Background";
import { Header } from "../../features/kquant/components/Header";
import { HeroSection } from "./HeroSection";
import { HotTopicsSection } from "./HotTopicsSection";
import { MarketAnalysisSection } from "./MarketAnalysisSection";
import { MarketRecapSection } from "./MarketRecapSection";
import { Toast, ToastState } from "../../features/kquant/components/Toast";
import { MapFilter, MarketTab } from "../../features/kquant/components/MarketHeatmapCard";

export default function KQuantSmartInsightsPage() {
  const [hotTopics, setHotTopics] = useState<HotTopicCard[] | null>(null);
  const [categories, setCategories] = useState<NewsCategoryOption[]>([]);
  const [selections, setSelections] = useState<SelectedCategory[]>([
    { key: "finance", count: 3 },
    { key: "energy", count: 3 },
    { key: "top", count: 3 },
  ]);
  const [preview, setPreview] = useState<PreviewNewsCard | null>(null);

  const [range, setRange] = useState<MarketIndex["range"]>("1D");
  const [index, setIndex] = useState<MarketIndex | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapNode | null>(null);

  const [marketTab, setMarketTab] = useState<MarketTab>("Overview");
  const [mapFilter, setMapFilter] = useState<MapFilter>("S&P 500");
  const [recap, setRecap] = useState<MarketRecap | null>(null);

  const [query, setQuery] = useState("");
  const [toast, setToast] = useState<ToastState>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [cats, hot, idx, hm, rc] = await Promise.all([
          mockApi.getCategories(),
          mockApi.getHotTopics(),
          mockApi.getMarketIndex("1D"),
          mockApi.getHeatmap(),
          mockApi.getMarketRecap(),
        ]);
        if (!alive) return;
        setCategories(cats.categories);
        setHotTopics(hot.items);
        setIndex(idx.index);
        setHeatmap(hm.tree);
        setRecap(rc.recap);

        const pv = await mockApi.previewNews(selections);
        if (!alive) return;
        setPreview(pv.card);
      } catch (e) {
        if (!alive) return;
        setToast({
          title: "Smart Insights",
          body: "初始化数据失败，请稍后重试。",
        });
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const idx = await mockApi.getMarketIndex(range);
        if (!alive) return;
        setIndex(idx.index);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [range]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const hm = await mockApi.getHeatmap();
        if (!alive) return;
        setHeatmap(hm.tree);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    })();
    return () => {
      alive = false;
    };
  }, [marketTab, mapFilter]);

  async function onPreview() {
    try {
      const pv = await mockApi.previewNews(selections);
      setPreview(pv.card);
    } catch (e) {
      setToast({ title: "Smart Insights", body: "Preview 失败，请稍后重试。" });
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  async function onAnalyze() {
    try {
      const res = await mockApi.analyzeQuery(query);
      setToast({ title: "Smart Insights", body: res.answer, followups: res.followups });
    } catch (e) {
      setToast({ title: "Smart Insights", body: "Analyze 失败，请稍后重试。" });
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#050514] text-white">
      <FormControlStyles />
      <Background />

      <div className="relative mx-auto max-w-[1280px] px-8">
        <Header />

        <main className="pb-16">
          <HeroSection query={query} setQuery={setQuery} onAnalyze={onAnalyze} />

          <HotTopicsSection
            categories={categories}
            selections={selections}
            setSelections={setSelections}
            hotTopics={hotTopics}
            preview={preview}
            onPreview={onPreview}
          />

          <MarketAnalysisSection
            index={index}
            range={range}
            setRange={setRange}
            heatmap={heatmap}
            marketTab={marketTab}
            setMarketTab={setMarketTab}
            mapFilter={mapFilter}
            setMapFilter={setMapFilter}
          />

          <MarketRecapSection recap={recap} />
        </main>
      </div>

      <Toast
        toast={toast}
        onClose={() => setToast(null)}
        onPickFollowup={(text) => {
          setQuery(text);
          setToast(null);
        }}
      />
    </div>
  );
}
